import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Routes, Route, Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Spin, Typography, Space, Button, Drawer, ConfigProvider } from 'antd';
import {
  HomeOutlined, ShoppingOutlined, CalendarOutlined,
  PhoneOutlined, MenuOutlined,
  ReadOutlined, FileTextOutlined, QuestionCircleOutlined,
  SoundOutlined, UserOutlined,
} from '@ant-design/icons';
import { scenarioSiteAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { getAntdThemeTokens } from './templateStyles';
import ScenarioSiteHome from './ScenarioSiteHome';
import ScenarioSiteProduct from './ScenarioSiteProduct';
import ScenarioSiteEvents from './ScenarioSiteEvents';
import ScenarioSiteContact from './ScenarioSiteContact';
import ScenarioSiteLOI from './ScenarioSiteLOI';

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function getLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function darkenColor(hex, amount = 0.2) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(b * f)})`;
}

function getHeaderBg(template, color) {
  const lum = getLuminance(color);
  if (template === 'minimal') return '#ffffff';
  if (template === 'classic') {
    return lum > 0.55 ? darkenColor(color, 0.35) : color;
  }
  const base = lum > 0.55 ? darkenColor(color, 0.3) : color;
  return `linear-gradient(135deg, ${base}, ${darkenColor(base, 0.15)})`;
}

function getTemplateStyles(template, brandColor) {
  const color = brandColor || '#1890ff';
  const lum = getLuminance(color);
  const headerDark = lum > 0.55 ? darkenColor(color, 0.35) : color;

  switch (template) {
    case 'classic':
      return {
        headerBg: getHeaderBg('classic', color),
        headerShadow: 'none',
        headerBorder: 'none',
        contentBg: '#ffffff',
        footerBg: headerDark,
        footerTextColor: 'rgba(255,255,255,0.85)',
        footerSubColor: 'rgba(255,255,255,0.55)',
        footerPoweredColor: 'rgba(255,255,255,0.35)',
        stickyCta: { background: headerDark, color: '#fff', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
        brandStyle: { fontWeight: 800, letterSpacing: 1 },
      };
    case 'minimal':
      return {
        headerBg: '#ffffff',
        headerShadow: 'none',
        headerBorder: '1px solid #e8e8e8',
        contentBg: '#fafafa',
        footerBg: '#ffffff',
        footerTextColor: '#333',
        footerSubColor: '#999',
        footerPoweredColor: '#ccc',
        stickyCta: { background: '#fff', color: color, borderRadius: 0, border: `2px solid ${color}`, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
        brandStyle: { fontWeight: 400, letterSpacing: 2, textTransform: 'uppercase' },
      };
    default:
      return {
        headerBg: getHeaderBg('modern', color),
        headerShadow: '0 2px 8px rgba(0,0,0,0.15)',
        headerBorder: 'none',
        contentBg: '#f5f7fa',
        footerBg: '#001529',
        footerTextColor: 'rgba(255,255,255,0.85)',
        footerSubColor: 'rgba(255,255,255,0.55)',
        footerPoweredColor: 'rgba(255,255,255,0.35)',
        stickyCta: { background: headerDark, color: '#fff', borderRadius: 28, boxShadow: '0 4px 16px rgba(0,0,0,0.2)' },
        brandStyle: { fontWeight: 700 },
      };
  }
}

function NavBar({ items, selectedKey, isMinimal, brandColor, isMobile, onItemClick }) {
  const headerIsDark = !isMinimal;

  const baseColor = headerIsDark ? 'rgba(255,255,255,0.75)' : '#666';
  const activeColor = headerIsDark ? '#fff' : '#333';
  const activeBg = headerIsDark ? 'rgba(255,255,255,0.18)' : `${brandColor}12`;
  const hoverBg = headerIsDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5';

  return (
    <nav style={{
      display: 'flex',
      gap: isMobile ? 0 : 4,
      flexDirection: isMobile ? 'column' : 'row',
      flex: isMobile ? undefined : 1,
      justifyContent: isMobile ? 'flex-start' : 'center',
      alignItems: isMobile ? 'stretch' : 'center',
    }}>
      {items.map(item => {
        const isActive = item.key === selectedKey;
        const handleClick = (e) => {
          if (item.scrollTo && onItemClick) {
            e.preventDefault();
            onItemClick(item);
          }
        };
        return (
          <Link
            key={item.key}
            to={item.key}
            onClick={handleClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: isMobile ? '12px 16px' : '6px 14px',
              borderRadius: isMobile ? 0 : 6,
              fontSize: 14,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? activeColor : baseColor,
              background: isActive ? activeBg : 'transparent',
              textDecoration: 'none',
              transition: 'all 0.2s',
              borderLeft: isMobile && isActive ? `3px solid ${isMinimal ? brandColor : '#fff'}` : isMobile ? '3px solid transparent' : 'none',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = hoverBg; }}
            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
          >
            {item.icon}
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

export default function ScenarioSiteLayout() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [siteInfo, setSiteInfo] = useState(null);
  const [sectionTypes, setSectionTypes] = useState([]);
  const [hasProducts, setHasProducts] = useState(false);
  const [hasEvents, setHasEvents] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    (async () => {
      try {
        const [infoRes, secRes, prodRes, evtRes] = await Promise.all([
          scenarioSiteAPI.getInfo(slug),
          scenarioSiteAPI.getSections(slug, i18n.language).catch(() => ({ data: [] })),
          scenarioSiteAPI.getProducts(slug, i18n.language).catch(() => ({ data: [] })),
          scenarioSiteAPI.getEvents(slug, i18n.language).catch(() => ({ data: [] })),
        ]);
        setSiteInfo(infoRes.data);
        const types = (secRes.data || []).map(s => s.section_type);
        setSectionTypes([...new Set(types)]);
        setHasProducts((prodRes.data || []).length > 0);
        setHasEvents((evtRes.data || []).length > 0);
      } catch (e) {
        setError(e.message || 'Site not found');
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, i18n.language]);

  useEffect(() => {
    if (siteInfo && siteInfo.default_language) {
      const siteKey = `spo_site_lang_${slug}`;
      const userChose = sessionStorage.getItem(siteKey);
      if (!userChose && i18n.language !== siteInfo.default_language) {
        i18n.changeLanguage(siteInfo.default_language);
      }
    }
  }, [siteInfo, slug, i18n]);

  useEffect(() => {
    if (siteInfo) {
      const title = siteInfo.seo_title || siteInfo.brand_name || siteInfo.scenario_name || 'Site';
      document.title = title;
      return () => { document.title = 'Comboware marketing AI'; };
    }
  }, [siteInfo]);

  const template = siteInfo?.site_template || 'modern';
  const brandColor = siteInfo?.brand_color || '#1890ff';
  const ts = useMemo(() => getTemplateStyles(template, brandColor), [template, brandColor]);
  const antdTokens = useMemo(() => getAntdThemeTokens(template, brandColor), [template, brandColor]);

  const handleScrollToSection = useCallback((item) => {
    const basePath = `/s/${slug}`;
    if (location.pathname !== basePath) {
      navigate(basePath);
      setTimeout(() => {
        const el = document.getElementById(item.scrollTo);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    } else {
      const el = document.getElementById(item.scrollTo);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [slug, location.pathname, navigate]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center',
        flexDirection: 'column', gap: 16,
      }}>
        <Text type="secondary" style={{ fontSize: 48 }}>404</Text>
        <Text type="secondary">{t('publicSite.notFound')}</Text>
      </div>
    );
  }

  const isMinimal = template === 'minimal';
  const basePath = `/s/${slug}`;

  const navConfig = siteInfo?.nav_config || {};
  const navTabsCfg = navConfig.tabs || {};
  const isTh = i18n.language === 'th';

  const getNavLabel = (tabKey, defaultLabel) => {
    const cfg = navTabsCfg[tabKey];
    if (!cfg) return defaultLabel;
    if (isTh && cfg.label_th) return cfg.label_th;
    if (!isTh && cfg.label) return cfg.label;
    return defaultLabel;
  };

  const isNavVisible = (tabKey) => {
    const cfg = navTabsCfg[tabKey];
    return cfg ? cfg.visible !== false : true;
  };

  const NAV_DEFS = {
    home: { icon: <HomeOutlined />, defaultLabel: t('publicSite.nav.home'), getKey: () => basePath },
    products: { icon: <ShoppingOutlined />, defaultLabel: t('publicSite.nav.products'), getKey: () => `${basePath}/products`, needsData: () => hasProducts || sectionTypes.includes('product_grid') },
    events: { icon: <CalendarOutlined />, defaultLabel: t('publicSite.nav.events'), getKey: () => `${basePath}/events`, needsData: () => hasEvents || sectionTypes.includes('event_list') },
    survey: { icon: <FileTextOutlined />, defaultLabel: t('publicSite.nav.survey'), getKey: () => `${basePath}#section-survey`, scrollTo: 'section-survey', needsData: () => sectionTypes.includes('survey') },
    blog: { icon: <ReadOutlined />, defaultLabel: t('publicSite.nav.blog'), getKey: () => `${basePath}#section-blog`, scrollTo: 'section-blog', needsData: () => sectionTypes.includes('blog') },
    faq: { icon: <QuestionCircleOutlined />, defaultLabel: t('publicSite.nav.faq'), getKey: () => `${basePath}#section-faq`, scrollTo: 'section-faq', needsData: () => sectionTypes.includes('faq') },
    testimonial: { icon: <SoundOutlined />, defaultLabel: t('publicSite.nav.testimonial'), getKey: () => `${basePath}#section-testimonial`, scrollTo: 'section-testimonial', needsData: () => sectionTypes.includes('testimonial') },
    contact: { icon: <PhoneOutlined />, defaultLabel: t('publicSite.nav.contact'), getKey: () => `${basePath}/contact` },
  };

  const defaultOrder = ['home', 'products', 'events', 'survey', 'blog', 'faq', 'testimonial', 'contact'];
  const orderedKeys = Object.keys(navTabsCfg).length > 0
    ? [...defaultOrder].sort((a, b) => (navTabsCfg[a]?.order ?? 99) - (navTabsCfg[b]?.order ?? 99))
    : defaultOrder;

  const navItemData = [];
  for (const tabKey of orderedKeys) {
    const def = NAV_DEFS[tabKey];
    if (!def) continue;
    if (!isNavVisible(tabKey)) continue;
    if (def.needsData && !def.needsData()) continue;
    const item = {
      key: def.getKey(),
      icon: def.icon,
      label: getNavLabel(tabKey, def.defaultLabel),
    };
    if (def.scrollTo) item.scrollTo = def.scrollTo;
    navItemData.push(item);
  }

  const selectedKey = navItemData
    .filter(i => !i.scrollTo)
    .map(i => i.key)
    .filter(k => location.pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0] || basePath;

  return (
    <ConfigProvider theme={{
      token: antdTokens,
      components: {
        Input: { borderRadius: antdTokens.borderRadius },
        Button: { borderRadius: antdTokens.borderRadius },
        Card: { borderRadius: antdTokens.borderRadius },
        Select: { borderRadius: antdTokens.borderRadius },
      },
    }}>
      <Layout style={{ minHeight: '100vh' }}>
        <Header style={{
          background: ts.headerBg,
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: ts.headerShadow,
          borderBottom: ts.headerBorder,
          height: 56,
          lineHeight: '56px',
        }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setDrawerOpen(true)}
              style={{ color: isMinimal ? '#333' : '#fff', fontSize: 18 }}
            />
          )}

          <Link to={basePath} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
            {siteInfo.logo_url && (
              <img src={siteInfo.logo_url} alt="logo" style={{ height: 36, borderRadius: template === 'minimal' ? 0 : 4 }} />
            )}
            <span style={{
              color: isMinimal ? '#333' : '#fff',
              fontSize: isMobile ? 16 : 20,
              whiteSpace: 'nowrap',
              ...ts.brandStyle,
            }}>
              {siteInfo.brand_name || siteInfo.scenario_name}
            </span>
          </Link>

          {!isMobile && (
            <NavBar
              items={navItemData}
              selectedKey={selectedKey}
              isMinimal={isMinimal}
              brandColor={brandColor}
              isMobile={false}
              onItemClick={handleScrollToSection}
            />
          )}
          <LanguageSwitcher
            ghost={!isMinimal}
            size="small"
            style={isMinimal
              ? { color: '#666', borderColor: '#d9d9d9' }
              : { color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.35)' }
            }
            onLanguageChange={() => sessionStorage.setItem(`spo_site_lang_${slug}`, '1')}
          />
          {navConfig.login?.visible !== false && (
            <Button
              size="small"
              icon={<UserOutlined />}
              onClick={() => window.open('/login', '_blank', 'noopener,noreferrer')}
              style={isMinimal
                ? { color: brandColor, borderColor: brandColor, background: '#fff' }
                : { color: '#fff', background: 'rgba(255,255,255,0.15)', borderColor: 'rgba(255,255,255,0.4)', fontWeight: 600 }
              }
            >
              {isTh ? 'เข้าสู่ระบบ' : (i18n.language === 'en' ? 'Sign in' : '登入')}
            </Button>
          )}
        </Header>

        {isMobile && (
          <Drawer
            placement="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            width={260}
            title={siteInfo.brand_name || siteInfo.scenario_name}
          >
            <NavBar
              items={navItemData}
              selectedKey={selectedKey}
              isMinimal={true}
              brandColor={brandColor}
              isMobile={true}
              onItemClick={(item) => { handleScrollToSection(item); setDrawerOpen(false); }}
            />
          </Drawer>
        )}

        <Content style={{ background: ts.contentBg, minHeight: 'calc(100vh - 130px)' }}>
          <Routes>
            <Route index element={<ScenarioSiteHome slug={slug} siteInfo={siteInfo} />} />
            <Route path="products" element={<ScenarioSiteHome slug={slug} siteInfo={siteInfo} showAllProducts />} />
            <Route path="products/:productId" element={<ScenarioSiteProduct slug={slug} siteInfo={siteInfo} />} />
            <Route path="events" element={<ScenarioSiteEvents slug={slug} siteInfo={siteInfo} />} />
            <Route path="loi" element={<ScenarioSiteLOI slug={slug} siteInfo={siteInfo} />} />
            <Route path="contact" element={<ScenarioSiteContact slug={slug} siteInfo={siteInfo} />} />
          </Routes>
        </Content>

        {navConfig.sticky_cta !== false && (
          <Link to={`${basePath}/contact`} style={{ textDecoration: 'none' }}>
            <div style={{
              position: 'fixed', bottom: 24, right: 24, zIndex: 999,
              padding: '12px 24px',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8,
              ...ts.stickyCta,
            }}>
              <PhoneOutlined /> {t('publicSite.contact.stickyButton')}
            </div>
          </Link>
        )}

        <Footer style={{
          background: ts.footerBg,
          textAlign: 'center',
          padding: '20px 24px',
          borderTop: isMinimal ? '1px solid #e8e8e8' : 'none',
        }}>
          <Space direction="vertical" size={4}>
            <Text style={{ color: ts.footerTextColor, fontWeight: 600 }}>
              {siteInfo.company_name || siteInfo.brand_name || siteInfo.scenario_name}
            </Text>
            {siteInfo.contact_email && (
              <Text style={{ color: ts.footerSubColor, fontSize: 12 }}>
                {siteInfo.contact_email} {siteInfo.contact_phone && `| ${siteInfo.contact_phone}`}
              </Text>
            )}
            <Text style={{ color: ts.footerPoweredColor, fontSize: 11 }}>
              Powered by SPO Platform
            </Text>
          </Space>
        </Footer>
      </Layout>
    </ConfigProvider>
  );
}
